import AppBar from '@mui/material/AppBar';
import { Container, Grid, useTheme } from '@mui/material';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import { memo, useMemo } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import FooterLogo from 'app/layouts/whitelabel/components/footer/FooterLogo';
import { footerConfig, getFooterMenuWidths, SECONDARY_LOGO_MD, TERTIARY_LOGO_MD } from 'app/configs/footerConfig';
import { getLogo } from '../helpers/Generichelper';
import useWindowDimensions from 'utils/useWindowDimensions';
import SocialMediaIcons from '../../../shared-components/SocialMediaIcons/SocialMediaIcons';
import { getChannelSettingByKey } from '../../routes/generic/Helpers/GenericIndexHelper';
import FooterLayoutAdvanced from './FooterLayoutAdvanced';
import { useStylesFooterLayout } from '../styles/FooterStyles';
import { getNewBuyInsurancePathGeneric } from '../../routes/generic/Helpers/HomePageHelper';
import { getGALabel } from '../../routes/generic/Helpers/GenericHelper';

const MenuRow = styled.div`
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	justify-content: flex-start;
	padding-right: 2.4rem;
`;

const MenuHeader = styled.div`
	font-size: 1.6rem;
	font-weight: 700;
	line-height: 1.4;
	margin-bottom: 2rem;
	color: ${({ theme }) => theme.palette.footer.headerFontColor};
`;

const MenuItem = styled.div`
	margin: 0 0 1.2rem;
	font-size: 1.4rem;
	line-height: 1.6;
	color: ${({ theme }) => theme.palette.footer.fontColor};
`;

const LinkText = styled.span`
	color: ${({ theme }) => theme.palette.footer.fontColor} !important;
	transition: opacity 150ms ease;

	&:hover {
		opacity: 0.75;
	}
`;

// Depends on nothing in the component, so it lives beside the other style declarations rather than
// being rebuilt inside a body that re-renders on every resize pixel.
const linkStyle = {
	background: 'none',
	textDecoration: 'none',
	borderBottom: 'none'
};

function FooterSecondLogo({ tertiaryLogo, tertiaryLogoText, tertiaryLogoTextColor }) {
	const logo = {
		marginTop: '10px',
		height: 'auto',
		width: 'auto',
		maxHeight: '100px',
		maxWidth: '120px'
	};
	const figcaption = {
		marginTop: '5px',
		fontSize: '10px',
		textAlign: 'center',
		color: tertiaryLogoTextColor ?? 'black'
	};
	const figContainer = {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		flexDirection: 'column'
	};

	return (
		<figure style={figContainer}>
			{tertiaryLogo && <img style={logo} src={tertiaryLogo} alt="" />}
			<figcaption style={figcaption}>{tertiaryLogoText}</figcaption>
		</figure>
	);
}

function FooterLayout(props) {
	const { width } = useWindowDimensions();
	const theme = useTheme();
	const classes = useStylesFooterLayout(width, theme);
	const channelSettings = useSelector(({ fuse }) => fuse.form.channelSettings);
	const companyDetailsResponse = useSelector(({ fuse }) => fuse.form.companyDetailsResponse);
	const agentDetails = useSelector(({ fuse }) => fuse?.form?.referenceTokenDetails?.agentDetails);
	const channelProducts = useSelector(({ fuse }) => fuse.form.channelProducts);
	const referenceTokenDetails = useSelector(({ fuse }) => fuse.form.referenceTokenDetails);
	const myHostPrefix = useSelector(({ fuse }) => fuse.form.mySite.hostPrefix);
	const retryPaymentResponse = useSelector(({ fuse }) => fuse.form.retryPaymentResponse);

	// footerConfig scans the whole channelSettings array through getSettings()'s 62-case switch and
	// then calls getChannelSettingByKey nine more times. useWindowDimensions is unthrottled, so
	// without this memo that entire derivation re-runs on every pixel of a window resize. The deps
	// are the store slices footerConfig reads internally via store.getState(), which is why they do
	// not appear in the callback body.
	const footerItems = useMemo(
		() => footerConfig({ companyDetailsResponse }),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[companyDetailsResponse, channelSettings, channelProducts, referenceTokenDetails, myHostPrefix]
	);
	const hideFooterMenuItems =
		retryPaymentResponse?.value || getChannelSettingByKey('HIDE-FOOTER-MENU-ITEMS') || 'false';
	const hideFirstFooter = getChannelSettingByKey('Footer-First-Hide') === 'true';
	const hideSecondFooter = getChannelSettingByKey('Footer-Second-Hide')
		? getChannelSettingByKey('Footer-Second-Hide') === 'true'
		: true;
	const hideThirdFooter = getChannelSettingByKey('Footer-Third-Hide')
		? getChannelSettingByKey('Footer-Third-Hide') === 'true'
		: true;
	const secondaryLogoSetting = getChannelSettingByKey('Secondary-Logo');
	const tertiaryLogoSetting = getChannelSettingByKey('Tertiary-Logo');
	const tertiaryLogoTextColor = getChannelSettingByKey('Footer-Logo-Text-Color');

	// getLogo rebuilds its result by scanning the entire channelSettings array, so it is called once
	// and destructured instead of once per field. agentLogoType 3 means the agent supplies its own
	// tertiary branding, which overrides both the channel's logo and its caption.
	const { tertiaryLogo: channelTertiaryLogo, tertiaryLogoText: channelTertiaryLogoText } = getLogo() ?? {};
	const hasAgentTertiaryLogo = agentDetails?.agentLogoType === 3;
	const tertiaryLogo =
		hasAgentTertiaryLogo && agentDetails?.agentLogoBlobUrl ? agentDetails.agentLogoBlobUrl : channelTertiaryLogo;
	const tertiaryLogoText =
		hasAgentTertiaryLogo && agentDetails?.agentLogoText ? agentDetails.agentLogoText : channelTertiaryLogoText;
	const buyInsurancePath = getNewBuyInsurancePathGeneric(channelProducts, referenceTokenDetails?.token, myHostPrefix);

	// Read through the local channelSettings subscription rather than getChannelSettingByKey, which
	// hits store.getState() directly and would not re-render the footer once settings arrive.
	const checkIsVisible = key => {
		return channelSettings?.value?.channelSettings.find(_key => _key.key === key)?.value;
	};

	// Every footer link is gated by its own channel setting. Holding the url -> setting map in one
	// place lets a column's emptiness be decided before render, so a channel that has hidden all of
	// a group's links no longer gets a heading floating over a blank column.
	const menuVisibilitySettingKeys = {
		'/faq': 'FooterMenu-FAQ-Visible',
		'/terms-and-conditions': 'FooterMenu-Terms-And-Conditions-Visible',
		'/privacy-policy': 'FooterMenu-PrivacyPolicy-Visible',
		'/disclosure': 'FooterMenu-Disclosure-Visible',
		'/details': 'FooterMenu-Details-Visible',
		'/contact': 'FooterMenu-Contact-Visible',
		'/about': 'FooterMenu-About-Visible',
		'/submit-claim': 'FooterMenu-Claim-Visible',
		[buyInsurancePath]: 'FooterMenu-Insurance-Visible'
	};

	// mailTo and phone entries carry no gate, matching the previous switch's default branch. Blank
	// titles are dropped because the address column emits empty rows for a channel that has no
	// phone number or email on file, and those become visible gaps at the new item spacing.
	const isMenuItemVisible = c => {
		if (!c?.title?.trim()) return false;
		if (c?.url?.slice(0, 6) === 'mailTo' || c?.url?.slice(0, 1) === '+') return true;

		const settingKey = menuVisibilitySettingKeys[c?.url];
		return settingKey ? checkIsVisible(settingKey) === 'true' : true;
	};

	// Drop whole columns, not just their links: an empty Grid item still reserves its md width and
	// still counts as a flex line, so it would leave a phantom gap in the row.
	const visibleMenus = (footerItems?.menus ?? [])
		.map(item => ({ ...item, menuItems: (item?.menuItems ?? []).filter(isMenuItemVisible) }))
		.filter(item => item.menuItems.length > 0);

	// Widths come from what will actually render, so hidden columns hand their space back to the
	// visible ones instead of leaving the row short of 12 and centred.
	const showMenus = hideFooterMenuItems === 'false';
	const menuWidths = getFooterMenuWidths({
		hasSecondaryLogo: Boolean(secondaryLogoSetting),
		hasTertiaryLogo: Boolean(tertiaryLogoSetting),
		menuCount: showMenus ? visibleMenus.length : 0
	});

	// A row that still falls short of 12 (a single menu, no logos) is centred rather than left
	// hanging against the container edge.
	const columnTotal =
		menuWidths.reduce((sum, menuWidth) => sum + menuWidth, 0) +
		(secondaryLogoSetting ? SECONDARY_LOGO_MD : 0) +
		(tertiaryLogoSetting ? TERTIARY_LOGO_MD : 0);

	// mailTo and tel targets leave the SPA, so they need a real anchor; every other url is an in-app
	// route and has to go through <Link> or it forces a full page reload. Only the routed branch
	// carries a GA label, which is what the previous per-branch markup did. The shared MenuItem
	// wrapper and the key stay with the caller so this returns the anchor alone.
	const renderFooterLink = c => {
		if (c?.url?.slice(0, 6) === 'mailTo') {
			return (
				<a href={c.url} target="_blank" rel="noopener noreferrer" style={linkStyle}>
					<LinkText theme={theme}>{c.title}</LinkText>
				</a>
			);
		}

		if (c?.url?.slice(0, 1) === '+') {
			return (
				<a href={c.url} style={classes.Phone}>
					<LinkText theme={theme}>{c.title}</LinkText>
				</a>
			);
		}

		return (
			<Link to={c.url || '/'}>
				<LinkText theme={theme} data-ga-label={getGALabel({ name: 'footer-menu', label: c?.title })}>
					{c.title}
				</LinkText>
			</Link>
		);
	};

	return (
		<>
			{!hideFirstFooter && (
				<AppBar
					id="fuse-footer"
					className={clsx('z-20 shadow-md', props.className)}
					position="relative"
					color="default"
					sx={{ backgroundColor: theme.palette.footer.background }}
				>
					<Container sx={{ px: { xs: 4, sm: 6 }, py: { xs: 5, md: 7 } }}>
						<Grid
							container
							justifyContent={columnTotal >= 12 ? 'flex-start' : 'center'}
							alignItems="flex-start"
							sx={{ rowGap: 5 }}
						>
							{secondaryLogoSetting && (
								<Grid item xs={12} sm={6} md={SECONDARY_LOGO_MD}>
									<FooterLogo width={width} />
								</Grid>
							)}
							{showMenus &&
								visibleMenus.map((item, itemIndex) => (
									<Grid item xs={item.xs} sm={item.sm} md={menuWidths[itemIndex]} key={item.id}>
										<MenuRow>
											<MenuHeader theme={theme}>{item.title}</MenuHeader>
											{item.menuItems.map(c => (
												<MenuItem theme={theme} key={c.url || c.title}>
													{renderFooterLink(c)}
												</MenuItem>
											))}
										</MenuRow>
									</Grid>
								))}
							{tertiaryLogoSetting && (
								<Grid className="flex flex-col items-center" item xs={12} sm={6} md={TERTIARY_LOGO_MD}>
									<SocialMediaIcons />
									<FooterSecondLogo
										tertiaryLogo={tertiaryLogo}
										tertiaryLogoText={tertiaryLogoText}
										tertiaryLogoTextColor={tertiaryLogoTextColor}
									/>
								</Grid>
							)}
						</Grid>

						<div style={classes.divider} />

						<div style={classes.recaptchaText}>
							This site is protected by reCAPTCHA and the Google
							<a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
								{' '}
								Privacy Policy
							</a>{' '}
							and
							<a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">
								{' '}
								Terms of Service
							</a>{' '}
							apply.
						</div>
					</Container>
				</AppBar>
			)}

			{/* Advanced Footer Layout */}
			{!hideSecondFooter || !hideThirdFooter ? (
				<div className="mt-10">
					<FooterLayoutAdvanced />
				</div>
			) : null}
		</>
	);
}

FooterSecondLogo.propTypes = {
	tertiaryLogo: PropTypes.string,
	tertiaryLogoText: PropTypes.string,
	tertiaryLogoTextColor: PropTypes.string
};

export default memo(FooterLayout);
